import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import BackMethods from "../../back_component/BackMethods";
import {
  FaCalendarAlt,
  FaUserTie,
  FaPlus,
  FaBuilding,
  FaClipboardCheck,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimesCircle,
  FaStar,
  FaHistory,
  FaTrash,
  FaEdit,
  FaChevronLeft,
  FaChevronRight,
  FaArrowRight,
} from "react-icons/fa";
import PatrolActivityModal from "./PatrolActivityModal";
import "../../styles/admin-shared.css";

const formatDateLabel = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value || "—";
  return date.toLocaleDateString("ar-SY", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
};

const RosterTotals = ({ totals }) => {
  const est = totals?.establishments ?? 0;
  const visits = totals?.inspectionVisits ?? 0;
  const viol = totals?.violationReports ?? 0;

  return (
    <div className="roster-totals" dir="rtl">
      <span
        className={`roster-total-chip roster-total-chip--est ${est > 0 ? "is-hot" : ""}`}
        title="منشآت"
      >
        <FaBuilding />
        <strong>{est}</strong>
        <small>منشآت</small>
      </span>
      <span
        className={`roster-total-chip roster-total-chip--visit ${visits > 0 ? "is-hot" : ""}`}
        title="زيارات"
      >
        <FaClipboardCheck />
        <strong>{visits}</strong>
        <small>زيارات</small>
      </span>
      <span
        className={`roster-total-chip roster-total-chip--viol ${viol > 0 ? "is-hot" : ""}`}
        title="ضبوط"
      >
        <FaExclamationTriangle />
        <strong>{viol}</strong>
        <small>ضبوط</small>
      </span>
    </div>
  );
};

const PatrolRosterManagementPage = () => {
  const [rosters, setRosters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  const [showActivityModal, setShowActivityModal] = useState(false);
  const [selectedActivityData, setSelectedActivityData] = useState(null);

  const [filterDate, setFilterDate] = useState("");
  const [filterStatus, setFilterStatus] = useState("active");

  const navigate = useNavigate();
  const { backEnd_request } = BackMethods();

  const fetchRosters = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        size: pageSize,
        status: filterStatus,
        date: filterDate || null,
      };

      const response = await backEnd_request.get("/PatrolRosters", { params });
      setRosters(response.data.data || []);
      setTotalItems(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, filterStatus, filterDate, backEnd_request]);

  useEffect(() => {
    fetchRosters();
  }, [fetchRosters]);

  const totalPages = Math.ceil(totalItems / pageSize);

  const handleViewActivity = async (roster) => {
    try {
      const response = await backEnd_request.get(
        `/admin/reports/activity/by-roster/${roster.patrolRosterId}`,
      );
      setSelectedActivityData(response.data);
      setShowActivityModal(true);
    } catch (error) {
      alert("فشل في جلب بيانات النشاط");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("هل أنت متأكد من حذف هذه الدورية؟")) return;
    try {
      await backEnd_request.delete(`/PatrolRosters/${id}`);
      fetchRosters();
    } catch (error) {
      console.error("Delete error:", error);
    }
  };

  return (
    <div
      className="admin-page"
      style={{ direction: "rtl", fontFamily: "Cairo, sans-serif" }}
    >
      <div className="container pb-4 pt-3 px-3 bg-white">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-2 pb-2 border-bottom gap-2">
          <div>
            <h3 className="fw-bold text-primary mb-0 d-flex align-items-center gap-2 fs-5">
              <span className="rounded-3 bg-primary bg-opacity-10 p-2 d-inline-flex text-primary">
                <FaCalendarAlt size={16} />
              </span>
              سجلات الدوريات
            </h3>
            <p className="text-muted small mb-0 mt-1">
              تنظيم ومتابعة طاقم العمليات الميدانية
            </p>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <button
              type="button"
              className="btn btn-outline-primary btn-sm rounded-pill px-3 fw-bold d-inline-flex align-items-center"
              onClick={() => navigate("/admin/patrol-activity-matrix")}
            >
              <FaHistory className="ms-1" /> نشاط الدوريات
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm rounded-pill px-3 fw-bold d-inline-flex align-items-center"
              onClick={() => navigate("/admin/patrol-rosters/create")}
            >
              <FaPlus className="ms-1" /> إضافة دورية
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm rounded-pill"
              onClick={() => navigate("/admin")}
            >
              <FaArrowRight className="ms-1" /> رجوع
            </button>
          </div>
        </div>

        <div className="admin-toolbar mb-2 bg-light rounded-3 border p-2">
          <div className="admin-count-chip">
            إجمالي السجلات
            <span>{totalItems}</span>
          </div>
          <div className="d-flex flex-wrap gap-2 align-items-center flex-grow-1 justify-content-md-end">
            <div
              className="input-group input-group-sm"
              style={{ maxWidth: 200 }}
            >
              <span className="input-group-text bg-white text-muted">
                التاريخ
              </span>
              <input
                type="date"
                className="form-control"
                value={filterDate}
                onChange={(e) => {
                  setFilterDate(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <select
              className="form-select form-select-sm"
              style={{ maxWidth: 130 }}
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="active">نشطة</option>
              <option value="unactive">منتهية</option>
              <option value="all">الكل</option>
            </select>
            <select
              className="form-select form-select-sm"
              style={{ maxWidth: 130 }}
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={10}>10 / صفحة</option>
              <option value={20}>20 / صفحة</option>
              <option value={50}>50 / صفحة</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="d-flex justify-content-center py-4">
            <div className="spinner-border text-primary" role="status"></div>
          </div>
        ) : (
          <>
            <div className="card border shadow-sm rounded-3 overflow-hidden">
              <div className="card-body p-0">
                <div className="table-responsive d-none d-lg-block">
                  <table className="table table-hover align-middle mb-0 roster-table">
                    <thead className="bg-light">
                      <tr className="text-secondary small">
                        <th className="ps-3 py-2">التاريخ</th>
                        <th className="py-2">المسؤول</th>
                        <th className="py-2">الطاقم</th>
                        <th className="py-2 text-center">النشاط</th>
                        <th className="py-2">الحالة</th>
                        {/* <th className="text-center py-2">الإجراءات</th> */}
                      </tr>
                    </thead>
                    <tbody>
                      {rosters.map((item) => (
                        <tr
                          key={item.patrolRosterId}
                          onClick={() => handleViewActivity(item)}
                        >
                          <td className="ps-3 py-2">
                            <div className="roster-date">
                              <span className="roster-date__label">
                                {formatDateLabel(item.patrolDate)}
                              </span>
                              <span className="roster-date__iso">
                                {item.patrolDate}
                              </span>
                            </div>
                          </td>
                          <td className="py-2">
                            <div className="d-flex align-items-center">
                              <div className="avatar-sm bg-soft-primary text-primary rounded-circle ms-2 d-flex align-items-center justify-content-center fw-bold">
                                {
                                  (item.user?.username ||
                                    item.user?.firstName ||
                                    "?")[0]
                                }
                              </div>
                              <div>
                                <div className="fw-bold small">
                                  {item.user?.username ||
                                    `${item.user?.firstName || ""} ${item.user?.lastName || ""}`.trim()}
                                </div>
                                {(item.user?.governorate ||
                                  item.user?.city) && (
                                  <div className="text-muted x-small">
                                    {[item.user.governorate, item.user.city]
                                      .filter(Boolean)
                                      .join(" · ")}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="py-2">
                            <div className="d-flex flex-wrap gap-1">
                              {(item.members || []).map((m) => (
                                <span
                                  key={m.patrolRosterMemberId}
                                  className={`badge roster-badge ${m.isLeader ? "leader" : "member"}`}
                                  onClick={() => setSelectedMember(m)}
                                  data-bs-toggle="modal"
                                  data-bs-target="#memberModal"
                                  title={`${m.firstName} ${m.fatherName || ""} ${m.lastName || ""}`.trim()}
                                >
                                  {m.isLeader && (
                                    <FaStar className="ms-1" size={10} />
                                  )}
                                  {m.firstName}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="py-2 text-center">
                            <RosterTotals totals={item.totals} />
                          </td>
                          <td className="py-2">
                            {item.isActive ? (
                              <span className="badge-status active">
                                <FaCheckCircle className="ms-1" /> نشطة
                              </span>
                            ) : (
                              <span className="badge-status inactive">
                                <FaTimesCircle className="ms-1" /> منتهية
                              </span>
                            )}
                          </td>
                          {/* <td className="text-center py-2">
                            <div className="d-flex justify-content-center gap-1">
                              <button
                                className="btn btn-sm btn-action text-info"
                                title="عرض النشاط"
                                onClick={() => handleViewActivity(item)}
                              >
                                <FaHistory size={16} />
                              </button>
                              <button
                                className="btn btn-sm btn-action text-primary"
                                onClick={() =>
                                  navigate(
                                    `/admin/patrol-rosters/edit/${item.patrolRosterId}`,
                                  )
                                }
                              >
                                <FaEdit size={15} />
                              </button>
                              <button
                                className="btn btn-sm btn-action text-danger"
                                onClick={() =>
                                  handleDelete(item.patrolRosterId)
                                }
                              >
                                <FaTrash size={15} />
                              </button>
                            </div>
                          </td> */}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="d-lg-none p-2">
                  {rosters.map((item) => (
                    <div
                      key={item.patrolRosterId}
                      className="card border shadow-sm rounded-3 mb-2"
                    >
                      <div className="card-body p-2">
                        <div className="d-flex justify-content-between align-items-start mb-1">
                          <div>
                            <div className="fw-bold text-primary small">
                              {formatDateLabel(item.patrolDate)}
                            </div>
                            <div className="text-muted x-small">
                              {item.patrolDate}
                            </div>
                          </div>
                          {item.isActive ? (
                            <span className="badge-status active">نشطة</span>
                          ) : (
                            <span className="badge-status inactive">
                              منتهية
                            </span>
                          )}
                        </div>
                        <div className="mb-2">
                          <span className="fw-semibold small">
                            {item.user?.username ||
                              `${item.user?.firstName || ""} ${item.user?.lastName || ""}`.trim()}
                          </span>
                        </div>
                        <div className="mb-2">
                          <RosterTotals totals={item.totals} />
                        </div>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-outline-info btn-sm flex-grow-1"
                            onClick={() => handleViewActivity(item)}
                          >
                            نشاط
                          </button>
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() =>
                              navigate(
                                `/admin/patrol-rosters/edit/${item.patrolRosterId}`,
                              )
                            }
                          >
                            <FaEdit />
                          </button>
                          <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleDelete(item.patrolRosterId)}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {totalPages > 1 && (
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-2 bg-light border p-2 rounded-3 gap-2">
                <div className="text-muted small">
                  عرض الصفحة <b>{currentPage}</b> من أصل <b>{totalPages}</b>
                </div>
                <nav>
                  <ul className="pagination pagination-sm mb-0 gap-1">
                    <li
                      className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                    >
                      <button
                        className="page-link rounded-circle border-0 shadow-sm"
                        onClick={() => setCurrentPage((p) => p - 1)}
                      >
                        <FaChevronRight size={11} />
                      </button>
                    </li>
                    {[...Array(totalPages)].map((_, i) => {
                      const n = i + 1;
                      if (
                        n === 1 ||
                        n === totalPages ||
                        (n >= currentPage - 1 && n <= currentPage + 1)
                      ) {
                        return (
                          <li
                            key={n}
                            className={`page-item ${currentPage === n ? "active" : ""}`}
                          >
                            <button
                              className="page-link rounded-pill border-0 mx-1 shadow-sm"
                              onClick={() => setCurrentPage(n)}
                            >
                              {n}
                            </button>
                          </li>
                        );
                      }
                      if (n === currentPage - 2 || n === currentPage + 2) {
                        return (
                          <li
                            key={n}
                            className="align-self-end px-1 text-muted"
                          >
                            ...
                          </li>
                        );
                      }
                      return null;
                    })}
                    <li
                      className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                    >
                      <button
                        className="page-link rounded-circle border-0 shadow-sm"
                        onClick={() => setCurrentPage((p) => p + 1)}
                      >
                        <FaChevronLeft size={11} />
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </>
        )}

        <PatrolActivityModal
          show={showActivityModal}
          onHide={() => setShowActivityModal(false)}
          data={selectedActivityData}
        />

        <div
          className="modal fade"
          id="memberModal"
          tabIndex="-1"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-centered modal-sm">
            <div className="modal-content border-0 shadow-lg rounded-4 text-center">
              <div className="modal-body p-3">
                {selectedMember && (
                  <>
                    <div
                      className={`avatar-lg mx-auto mb-2 rounded-circle d-flex align-items-center justify-content-center ${selectedMember.isLeader ? "bg-warning-subtle text-warning" : "bg-primary-subtle text-primary"}`}
                      style={{ width: 60, height: 60 }}
                    >
                      <FaUserTie size={26} />
                    </div>
                    <h5 className="fw-bold mb-1 fs-6">
                      {selectedMember.firstName} {selectedMember.lastName}
                    </h5>
                    <p className="text-muted small mb-2">
                      {selectedMember.departmentName || "بدون قسم"}
                    </p>
                    <button
                      className="btn btn-dark btn-sm w-100 rounded-pill"
                      data-bs-dismiss="modal"
                    >
                      إغلاق
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <style>{`
                    .bg-soft-primary { background-color: #e7f1ff; }
                    .x-small { font-size: 0.75rem; }
                    .avatar-sm { width: 32px; height: 32px; font-size: 0.8rem; flex-shrink: 0; }
                    .roster-date { display: flex; flex-direction: column; line-height: 1.2; }
                    .roster-date__label { font-weight: 800; color: #0f172a; font-size: 0.88rem; }
                    .roster-date__iso { font-size: 0.72rem; color: #64748b; font-weight: 600; }
                    .roster-totals {
                        display: inline-flex;
                        flex-wrap: wrap;
                        gap: 4px;
                        justify-content: center;
                    }
                    .roster-total-chip {
                        display: inline-flex;
                        align-items: center;
                        gap: 3px;
                        padding: 3px 7px;
                        border-radius: 999px;
                        font-size: 0.72rem;
                        font-weight: 700;
                        border: 1px solid transparent;
                        background: #f8fafc;
                        color: #64748b;
                        white-space: nowrap;
                    }
                    .roster-total-chip strong {
                        font-variant-numeric: tabular-nums;
                        font-size: 0.82rem;
                    }
                    .roster-total-chip small {
                        font-size: 0.65rem;
                        font-weight: 600;
                        opacity: 0.9;
                    }
                    .roster-total-chip--est { color: #1d4ed8; border-color: #bfdbfe; background: #eff6ff; }
                    .roster-total-chip--visit { color: #15803d; border-color: #bbf7d0; background: #f0fdf4; }
                    .roster-total-chip--viol { color: #64748b; border-color: #e2e8f0; background: #f8fafc; }
                    .roster-total-chip--viol.is-hot { color: #b91c1c; border-color: #fecaca; background: #fef2f2; }
                    .roster-total-chip--est.is-hot,
                    .roster-total-chip--visit.is-hot { font-weight: 800; }
                    .roster-badge { cursor: pointer; padding: 4px 8px; border-radius: 6px; transition: 0.2s; font-weight: 500; font-size: 0.78rem; }
                    .roster-badge.leader { background-color: #fff9db; color: #856404; border: 1px solid #ffeeba; }
                    .roster-badge.member { background-color: #f8f9fa; color: #495057; border: 1px solid #e9ecef; }
                    .badge-status { padding: 4px 10px; border-radius: 20px; font-size: 0.72rem; font-weight: bold; }
                    .badge-status.active { background-color: #d4edda; color: #155724; }
                    .badge-status.inactive { background-color: #f8d7da; color: #721c24; }
                    .btn-action { background: none; border: none; padding: 4px 8px; border-radius: 5px; transition: 0.2s; }
                    .btn-action:hover { background-color: #f1f1f1; }
                    .page-item.active .page-link { background-color: #0d6efd; color: white; }
                    .page-link { color: #0d6efd; }
                    .roster-table tbody tr:hover { background: #f8fafc; }
                `}</style>
      </div>
    </div>
  );
};

export default PatrolRosterManagementPage;
